/**
 * iter87.213.3 — MLB Playoff Standings.
 *
 * Round-by-round bracket view for the MLB Playoffs template. Built from
 * scratch (does NOT reuse the generic BracketView.jsx) because MLB's data
 * shape is best-of-N `series` objects with a `games[]` list, nested under
 * `tournament.mlb.rounds` and served by `GET /mlb/tournaments/{id}/bracket`
 * — architecturally different from the scheduled_matches + stage_reached
 * shape every other knockout template uses.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

const LEAGUE_PILL = {
    AL: "bg-heat/10 border border-heat/30 text-heat",
    NL: "bg-volt/10 border border-volt/30 text-volt",
};

export default function MlbPlayoffStandings({ tournamentId, teams }) {
    const [bracket, setBracket] = useState(null);

    const nameFor = (id) => (teams || []).find((t) => t.id === id)?.name || "TBD";
    const seedFor = (id) => (teams || []).find((t) => t.id === id)?.seed;

    useEffect(() => {
        if (!tournamentId) return;
        api.getMlbBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [tournamentId]);

    if (!bracket || !bracket.rounds?.length) {
        return (
            <div className="brutal-card p-4 sm:p-5 text-center text-zinc-500 text-xs uppercase tracking-widest" data-testid="mlb-standings-empty">
                Playoff bracket not synced yet
            </div>
        );
    }

    const record = (s) => {
        const wins = (id) => (s.games || []).filter((g) => g.played && (
            (g.score_a > g.score_b && id === s.team_a_id) || (g.score_b > g.score_a && id === s.team_b_id)
        )).length;
        return { a: wins(s.team_a_id), b: wins(s.team_b_id) };
    };

    const TeamRow = ({ id, isWinner, isLoser, score }) => (
        <div
            className={`flex items-center justify-between px-2 py-2 rounded-lg border transition-colors ${isWinner ? "bg-cyber/10 border-cyber/30" : "border-transparent"}`}
        >
            <div className="flex items-center min-w-0 gap-2">
                <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-white/[0.04] border border-white/[0.08] font-mono text-[9px] font-bold text-zinc-400 shrink-0">
                    {seedFor(id) ?? "–"}
                </span>
                {/* iter87.272 (user-requested) — team swatch was missing
                    from the bracket rows entirely (only the standings
                    modal had it) — added back here too. */}
                <TeamSwatch name={nameFor(id)} sport="mlb" size="w-6 h-6 text-[9px]" />
                <span className={`text-sm font-semibold truncate flex-1 ${isWinner ? "text-cyber" : isLoser ? "text-zinc-500 line-through" : "text-zinc-200"}`}>
                    {nameFor(id)}
                </span>
            </div>
            {/* iter87.272 — labeled: this is the team's win count WITHIN
                this best-of-N series (not season wins), was a bare
                unlabeled number. */}
            <span className={`font-mono font-black text-sm tabular-nums ml-2 shrink-0 ${isWinner ? "text-white" : "text-zinc-500"}`}>
                {score}<span className="text-[9px] font-bold text-zinc-500 ml-0.5">W</span>
            </span>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="mlb-playoff-standings">
            {bracket.rounds.map((r) => {
                const series = r.series || [];
                if (!series.length) return null;
                return (
                    <div key={r.key} data-testid={`mlb-standings-round-${r.key}`}>
                        <h4 className="font-display font-extrabold uppercase text-base sm:text-lg tracking-tight mb-3 text-white border-b border-white/[0.08] pb-2">
                            {r.label}
                        </h4>
                        <div className="space-y-3">
                            {series.map((s) => {
                                const rec = record(s);
                                const isFinal = s.status === "final";
                                const isOpen = s.status === "open" || s.status === "locked";
                                return (
                                    <div
                                        key={s.id}
                                        data-testid={`mlb-standings-series-${s.id}`}
                                        className={`brutal-card-light border border-white/[0.08] p-3 rounded-lg flex flex-col gap-1.5 ${s.status === "tbd" ? "opacity-60 border-dashed" : ""}`}
                                    >
                                        <div className="flex items-center justify-between pb-2 mb-1 border-b border-white/[0.04]">
                                            {s.league ? (
                                                <span className={`inline-flex items-center rounded-full text-[9px] font-bold px-2 py-0.5 tracking-wider uppercase ${LEAGUE_PILL[s.league] || "bg-white/[0.04] border border-white/10 text-zinc-400"}`}>
                                                    {s.league}
                                                </span>
                                            ) : <span />}
                                            {s.status === "tbd" && (
                                                <span className="text-[9px] uppercase tracking-widest text-zinc-500">TBD</span>
                                            )}
                                            {s.projected && (
                                                <span
                                                    data-testid={`mlb-standings-series-${s.id}-projected-badge`}
                                                    className="pill bg-neutral-800 text-neutral-400 border border-neutral-700 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest"
                                                >
                                                    Projected
                                                </span>
                                            )}
                                            {isOpen && !s.projected && (
                                                <span className="text-[9px] uppercase tracking-widest font-bold flex items-center gap-1">
                                                    {(rec.a || rec.b) ? (
                                                        <span className="pill bg-heat text-white px-2 py-0.5 flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                                            {`GAME ${(s.games || []).filter((g) => g.played).length + 1}`}
                                                        </span>
                                                    ) : (
                                                        <span className="pill bg-volt text-black px-2 py-0.5">UPCOMING</span>
                                                    )}
                                                </span>
                                            )}
                                            {isFinal && (
                                                <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">FINAL</span>
                                            )}
                                        </div>
                                        {s.team_a_id && (
                                            <TeamRow id={s.team_a_id} isWinner={isFinal && s.winner_team_id === s.team_a_id}
                                                     isLoser={isFinal && s.winner_team_id !== s.team_a_id} score={rec.a} />
                                        )}
                                        {s.team_b_id && (
                                            <TeamRow id={s.team_b_id} isWinner={isFinal && s.winner_team_id === s.team_b_id}
                                                     isLoser={isFinal && s.winner_team_id !== s.team_b_id} score={rec.b} />
                                        )}
                                        {!s.team_a_id && !s.team_b_id && (
                                            <p className="text-xs text-zinc-500 py-2">Matchup not set yet</p>
                                        )}
                                        {isFinal && (
                                            <p data-testid={`mlb-standings-series-${s.id}-summary`} className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mt-1 pt-2 border-t border-white/[0.04]">
                                                {nameFor(s.winner_team_id)} wins {Math.max(rec.a, rec.b)}-{Math.min(rec.a, rec.b)}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
